import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { BRANCH_PICKER, BRANCH_STORAGE_KEY, BRANCHES, MAIN_BRANCH, branchByKey, type Branch } from '../config/branches';
import { API_CONFIG } from '../services/config';

/**
 * Sucursal elegida por el visitante. Solo existe eleccion con el interruptor
 * de sucursales encendido (src/config/branches.ts): en la web publica actual
 * siempre es la principal y no se guarda nada.
 */
interface BranchContextValue {
  /** ¿Se muestra el selector? */
  enabled: boolean;
  branches: Branch[];
  branch: Branch;
  setBranch: (key: string) => void;
}

const BranchContext = createContext<BranchContextValue | null>(null);
const STORAGE_KEY = BRANCH_STORAGE_KEY;

function loadKey(): string | null {
  if (!BRANCH_PICKER) return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function BranchProvider({ children }: { children: ReactNode }) {
  const [key, setKey] = useState<string | null>(loadKey);

  const setBranch = useCallback((next: string) => {
    setKey(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // sin almacenamiento: la eleccion dura lo que la pestaña
    }
  }, []);

  return (
    <BranchContext.Provider
      value={{
        enabled: BRANCH_PICKER,
        branches: BRANCHES,
        branch: BRANCH_PICKER ? branchByKey(key) : MAIN_BRANCH,
        setBranch,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch(): BranchContextValue {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error('useBranch must be used inside BranchProvider');
  return ctx;
}

/** WhatsApp de una sucursal; si aun no tiene numero propio, el general. */
export function branchWhatsApp(branch: Branch): string {
  return branch.whatsapp || API_CONFIG.WHATSAPP_NUMBER;
}
